// we're loading the wasm file here to have it globally loaded already when it's needed
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import React, {
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
} from "react";

import Navigation from "@brexhq/next/src/components/navigation";
import {
  AnnouncementBanner,
  Banner,
} from "@brexhq/next/src/components/shared/AnnouncementBanner/AnnouncementBanner";
import Footer from "@brexhq/next/src/components/shared/Footer/Footer";
import type { AdditionalLegalFooterSectionProps } from "@brexhq/next/src/components/shared/Footer/types";
import { PartnerBanner } from "@brexhq/next/src/components/shared/PartnerBanner/PartnerBanner";
import { SiteContext } from "@brexhq/next/src/contexts/Site";
import { getFooterById, getNavById } from "@brexhq/next/src/data/dataUtils";
import {
  allBanners,
  selectedBannerId,
} from "@brexhq/next/src/data/siteSettings";
import updateCookie from "@brexhq/next/src/utils/updateCookies";
import type { SpacingValues } from "@brexhq/shared/types/SpacingScale";
import { isDev } from "@brexhq/shared/utils/env/isDevEnv";
import { PageType } from "@brexhq/shared/utils/sanity/pageTypes";

import { bannerExperiment } from "../src/config/experimentation/data";
import { selectedFooterId } from "../src/data/footer";
import { selectedNavId } from "../src/data/navigation";
import { getServerSideCurrentSlug } from "../src/utils/getServerSideCurrentUrl";

import JsonLd from "./layoutJsonld";

// If theres an experiment we set the banner to the default value of that, if no experiment its the banner set in site settings
const bannerId = bannerExperiment
  ? bannerExperiment.references[bannerExperiment.defaultParameterValue].docId
  : selectedBannerId;
const announcementBanner = bannerId ? allBanners[bannerId] : null;

export type LayoutProps = {
  refCode?: string;
  hideDefaultHeader?: boolean;
  bannerOptions?: { alwaysHideBanner?: boolean };
  jsonLd?: boolean | null;
  pageType: PageType;
  footerOptions: {
    hideDefaultFooter?: boolean;
    topSpacing?: SpacingValues;
    additionalLegalFooterSection?: AdditionalLegalFooterSectionProps;
    hideFooterLinks?: boolean;
    hideFooterSecondaryLinks?: boolean;
  };
  seoData: {
    description: string;
    title: string;
    ogImage?: string;
    indexPage?: boolean;
  };
  darkModeNav?: boolean;
};

const StagingBanner = dynamic(
  () =>
    import("@brexhq/next/src/components/shared/staging-banner/StagingBanner"),
  {},
);

const Layout: FC<PropsWithChildren<any>> = ({ children }) => {
  let layoutProps: LayoutProps = children?.props?.layoutProps;
  const updatedAt = children?.props?.updatedAt;
  const articleDate =
    children?.props?.data?.contentHubArticleContent?.articleDate;
  // JSON-LD is enabled by default for all pages, and is disabled if jsonLd Sanity field is explicitly set to false
  const disableJsonLd =
    typeof layoutProps?.jsonLd === "boolean" && !layoutProps?.jsonLd;

  // Default Nav Data - experiment nav data is handled inside the component
  const navData = getNavById(selectedNavId);
  // Default Footer Data - experiment footer data is handled inside the component
  const footerData = getFooterById(selectedFooterId);

  if (!layoutProps) {
    if (isDev) console.error("did you forget to pass down layout props?");
    layoutProps = {
      pageType: "corePage",
      hideDefaultHeader: false,
      footerOptions: {
        hideDefaultFooter: false,
        topSpacing: "lrg",
      },
      seoData: {
        description:
          "Brex offers everything you need to grow your company faster, from cards and capital to built-in spend management software and live support.",
        title: "Finance for fast-growing companies",
        indexPage: true,
      },
    };
  }

  const { refCode, hideDefaultHeader, footerOptions, seoData } = layoutProps;

  const { partnerBanner } = useContext(SiteContext);
  const router = useRouter();

  useEffect(() => {
    if (refCode) {
      // if explicit refcode set via sanity use it
      // unless a partner refcode is set in the context as then the ref code has already been set so we skip it in here

      if (partnerBanner.response?.ref_code) return;
      updateCookie("ref_code", refCode);
    }
  }, [refCode, partnerBanner]);

  // Proxy OG image when it comes from Bynder so it can be used in social media previews, else use default ogImage defined in static.
  const ogImageUrl = seoData.ogImage
    ? seoData.ogImage.includes("brand.brex.com")
      ? `https://www.brex.com/api/proxy-image?url=${encodeURIComponent(
          `${seoData.ogImage}?io=transform:fit,width:1200`,
        )}`
      : `${seoData.ogImage}?io=transform:fit,width:1200`
    : "https://www.brex.com/ogImage.png";

  return (
    <>
      <NextSeo
        title={seoData.title}
        description={seoData.description}
        openGraph={{
          title: seoData.title,
          description: seoData.description,
          images: [
            // Set any image coming from bynder to be 1200px wide
            {
              url: ogImageUrl,
            },
          ],
        }}
        twitter={{
          cardType: "summary_large_image",
          site: "@brexHQ",
        }}
        noindex={isDev ? true : !seoData.indexPage}
      />
      {!disableJsonLd && (
        <JsonLd
          seoData={seoData}
          pageType={layoutProps.pageType}
          updatedAt={updatedAt}
          articleDate={articleDate}
          ogImageUrl={ogImageUrl}
        />
      )}
      {partnerBanner.isVisible && <PartnerBanner {...partnerBanner} />}
      {announcementBanner && (
        <AnnouncementBanner
          banner={announcementBanner as Banner}
          currentPageSlug={getServerSideCurrentSlug(router)}
          currentPageType={layoutProps.pageType}
          hidden={
            // Don't show announcement banner if the url has a partnerId
            partnerBanner.hasPartnerId ||
            // Don't show announcement banner if a user has already seen a partner banner
            Boolean(partnerBanner.response?.ref_code) ||
            // Don't show announcement banner if the current page is opted out of all banners
            Boolean(layoutProps.bannerOptions?.alwaysHideBanner)
          }
        />
      )}
      {!hideDefaultHeader && (
        <Navigation
          data={navData}
          currentPageSlug={router.asPath}
          darkModeNav={layoutProps.darkModeNav}
        />
      )}
      {/* Staging banner shown on preview environments */}
      <StagingBanner />

      <main>{children}</main>

      {!footerOptions.hideDefaultFooter && (
        <Footer
          data={footerData}
          hideFooterLinks={footerOptions.hideFooterLinks}
          hideFooterSecondaryLinks={footerOptions.hideFooterSecondaryLinks}
          topSpacing={footerOptions.topSpacing}
          additionalLegalFooterSection={
            footerOptions.additionalLegalFooterSection
          }
        />
      )}
    </>
  );
};

export default Layout;
